// filtri/shader.ts — il passaggio WebGL2 sul fotogramma intero (filtri di colore e deformazione).
// Il filtro scrive solo `vec4 effetto(vec2 uv)`; qui si aggiungono davanti le uniform e le funzioni comuni
// (PREAMBOLO, documentato in docs/filtri/SPECIFICA-FILTRI.md §5) e dietro il main, che PROTEGGE LA BOCCA:
// dentro la zona della bocca il risultato viene riportato verso l'immagine originale, qualunque cosa faccia il filtro.
import { ancore, boccaAperta, occhiChiusi, sopraccigliaAlzate, sorriso, zonaBocca, type Ellisse } from "./geometria.ts";
import type { ContestoFiltro, PassaggioShader } from "./tipi.ts";

export const PREAMBOLO = `#version 300 es
precision highp float;
in vec2 v_uv;
out vec4 l_colore;
uniform sampler2D u_immagine;
uniform vec2 u_dimensioni;      // larghezza e altezza in pixel
uniform float u_tempo;          // secondi dall'attivazione
uniform float u_riduci;         // 1 = riduci animazioni
uniform float u_volto;          // 1 = volto presente (se 0 le ancore valgono l'ultimo volto visto, o il centro)
uniform vec2 u_occhioDestro;    // ancore in uv (0..1, origine in alto a sinistra)
uniform vec2 u_occhioSinistro;
uniform vec2 u_puntaNaso;
uniform vec2 u_bocca;
uniform vec2 u_mento;
uniform vec2 u_fronte;
uniform vec2 u_guanciaDestra;
uniform vec2 u_guanciaSinistra;
uniform float u_scala;          // distanza tra gli occhi in PIXEL
uniform float u_rollio;         // radianti
uniform vec4 u_espressioni;     // x sorriso, y bocca aperta, z sopracciglia alzate, w occhi chiusi (0..1)
uniform vec4 u_zonaBocca;       // centro in uv (xy), semiassi in pixel (zw); ruotata di u_rollio

vec4 campiona(vec2 uv) { return texture(u_immagine, clamp(uv, 0.0, 1.0)); }
vec2 inPixel(vec2 uv) { return uv * u_dimensioni; }
vec2 inUv(vec2 px) { return px / u_dimensioni; }
/** Distanza in unità "s" (distanza tra gli occhi) tra due punti uv. */
float distanzaS(vec2 a, vec2 b) { return length((a - b) * u_dimensioni) / max(u_scala, 1.0); }
/** 1 dentro la zona della bocca, 0 fuori, bordo morbido. */
float inZonaBocca(vec2 uv) {
  vec2 d = (uv - u_zonaBocca.xy) * u_dimensioni;
  float c = cos(-u_rollio), s = sin(-u_rollio);
  vec2 r = vec2(d.x * c - d.y * s, d.x * s + d.y * c) / max(u_zonaBocca.zw, vec2(1.0));
  return 1.0 - smoothstep(0.85, 1.25, length(r));
}
/** Per le deformazioni: lo spostamento si annulla vicino alla bocca. */
vec2 spostamentoProtetto(vec2 uv, vec2 delta) { return delta * (1.0 - inZonaBocca(uv)); }
float luminanza(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }
float casuale2(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
/** v034: la bocca nello stile del filtro: il colore del filtro con la luce dell'originale (la bocca si legge sempre). */
vec3 boccaInStile(vec3 filtrato, vec3 originale) { return clamp(filtrato + vec3(luminanza(originale) - luminanza(filtrato)), 0.0, 1.0); }
`;

// v034: nella zona della bocca si riporta la LUCE dell'originale (denti chiari, labbra e bocca aperta scure: la bocca
// si legge sempre), non il suo colore: prima nei filtri di colore (bianco e nero, seppia, neon…) attorno alla bocca
// restava una macchia color pelle. Un filtro che coprisse tutto con un colore pieno mostra comunque la bocca.
const CHIUSURA = `
uniform float u_ripristinoBocca; // impostazione: quanto riportare la bocca all'originale (0..1)
void main() {
  vec4 c = effetto(v_uv);
  vec3 o = campiona(v_uv).rgb;
  l_colore = vec4(mix(c.rgb, boccaInStile(c.rgb, o), inZonaBocca(v_uv) * u_ripristinoBocca), 1.0);
}
`;

const VERTICI = `#version 300 es
in vec2 a_pos;
out vec2 v_uv;
void main() {
  v_uv = vec2(a_pos.x * 0.5 + 0.5, 0.5 - a_pos.y * 0.5);
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

/**
 * Uniform comuni del fotogramma (i nomi sono quelli del PREAMBOLO). Pura: si prova in node.
 * `zona`: la zona della bocca calcolata dal motore sui punti NON stabilizzati (segue subito i movimenti veloci).
 */
export function uniformiComuni(c: ContestoFiltro, ultime: Record<string, number[]> | null, zona?: Ellisse | null): Record<string, number[]> {
  const w = c.larghezza;
  const h = c.altezza;
  const uv = (p: { x: number; y: number }) => [p.x / w, p.y / h];
  const base: Record<string, number[]> = {
    u_dimensioni: [w, h],
    u_tempo: [c.tempoMs / 1000],
    u_riduci: [c.riduciAnimazioni ? 1 : 0],
    u_volto: [c.volto ? 1 : 0],
  };
  if (!c.volto) {
    const ripiego = ultime ?? {
      u_occhioDestro: [0.4, 0.4], u_occhioSinistro: [0.6, 0.4], u_puntaNaso: [0.5, 0.5], u_bocca: [0.5, 0.6],
      u_mento: [0.5, 0.7], u_fronte: [0.5, 0.25], u_guanciaDestra: [0.38, 0.52], u_guanciaSinistra: [0.62, 0.52],
      u_scala: [w * 0.2], u_rollio: [0], u_espressioni: [0, 0, 0, 0], u_zonaBocca: [0.5, 0.6, 0, 0],
    };
    return { ...ripiego, ...base, u_espressioni: [0, 0, 0, 0] };
  }
  const v = c.volto;
  const a = ancore(v);
  const z = zona ?? zonaBocca(v);
  return {
    ...base,
    u_occhioDestro: uv(a.occhioDestro),
    u_occhioSinistro: uv(a.occhioSinistro),
    u_puntaNaso: uv(a.puntaNaso),
    u_bocca: uv(a.bocca),
    u_mento: uv(a.mento),
    u_fronte: uv(a.fronte),
    u_guanciaDestra: uv(a.guanciaDestra),
    u_guanciaSinistra: uv(a.guanciaSinistra),
    u_scala: [v.posa.scala],
    u_rollio: [v.posa.rollio],
    u_espressioni: [sorriso(v.espressioni), boccaAperta(v.espressioni), sopraccigliaAlzate(v.espressioni), occhiChiusi(v.espressioni)],
    u_zonaBocca: [...uv(z.centro), z.rx, z.ry],
  };
}

/**
 * Esegue un PassaggioShader su un'immagine sorgente e disegna il risultato nel canvas WebGL dato.
 * Il canvas (e il suo contesto) può essere riusato da un passaggio all'altro: `libera()` toglie programma e
 * texture ma non il contesto; per chiudere anche quello si usa `chiudiContestoGL(canvas)`.
 */
export class PassaggioGL {
  readonly canvas: HTMLCanvasElement | OffscreenCanvas;
  private gl: WebGL2RenderingContext;
  private programma: WebGLProgram;
  private texture: WebGLTexture;
  private buffer: WebGLBuffer;
  private posizioni = new Map<string, WebGLUniformLocation | null>();
  private ultime: Record<string, number[]> | null = null;
  private shader: PassaggioShader;
  private ripristinoBocca: number;

  /** Lancia un errore (con il log del compilatore) se lo shader non compila; in quel caso non lascia nulla in giro. */
  constructor(canvas: HTMLCanvasElement | OffscreenCanvas, shader: PassaggioShader, ripristinoBocca: number) {
    this.canvas = canvas;
    this.shader = shader;
    this.ripristinoBocca = ripristinoBocca;
    const gl = canvas.getContext("webgl2", { premultipliedAlpha: false, preserveDrawingBuffer: false, antialias: false }) as WebGL2RenderingContext | null;
    if (!gl) throw new Error("WebGL2 non disponibile");
    if (gl.isContextLost()) throw new Error("contesto WebGL perso");
    this.gl = gl;
    const creati: WebGLShader[] = [];
    let p: WebGLProgram | null = null;
    try {
      const compila = (tipo: number, sorgente: string): WebGLShader => {
        const sh = gl.createShader(tipo)!;
        creati.push(sh);
        gl.shaderSource(sh, sorgente);
        gl.compileShader(sh);
        if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(`shader non compilato: ${gl.getShaderInfoLog(sh) ?? "?"}`);
        return sh;
      };
      p = gl.createProgram()!;
      gl.attachShader(p, compila(gl.VERTEX_SHADER, VERTICI));
      gl.attachShader(p, compila(gl.FRAGMENT_SHADER, PREAMBOLO + shader.codice + CHIUSURA));
      gl.linkProgram(p);
      if (!gl.getProgramParameter(p, gl.LINK_STATUS)) throw new Error(`shader non collegato: ${gl.getProgramInfoLog(p) ?? "?"}`);
    } catch (e) {
      if (p) gl.deleteProgram(p);
      for (const sh of creati) gl.deleteShader(sh);
      throw e;
    }
    for (const sh of creati) gl.deleteShader(sh); // restano vivi finché il programma li usa
    this.programma = p;
    this.buffer = gl.createBuffer()!;
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(p, "a_pos");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    this.texture = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }

  private imposta(nome: string, valori: readonly number[]): void {
    // Uniform sempre float/vec2..4: valori di lunghezza sbagliata o non finiti si ignorano (non diventano NaN).
    if (valori.length < 1 || valori.length > 4 || valori.some((n) => !Number.isFinite(n))) return;
    const gl = this.gl;
    if (!this.posizioni.has(nome)) this.posizioni.set(nome, gl.getUniformLocation(this.programma, nome));
    const l = this.posizioni.get(nome) ?? null;
    if (!l) return; // uniform non usata dallo shader: il compilatore l'ha tolta
    if (valori.length === 1) gl.uniform1f(l, valori[0]!);
    else if (valori.length === 2) gl.uniform2f(l, valori[0]!, valori[1]!);
    else if (valori.length === 3) gl.uniform3f(l, valori[0]!, valori[1]!, valori[2]!);
    else gl.uniform4f(l, valori[0]!, valori[1]!, valori[2]!, valori[3]!);
  }

  /** Lancia un errore se il contesto WebGL è perso (telefono in background, poca memoria): il motore spegne il filtro. */
  esegui(sorgente: TexImageSource, c: ContestoFiltro, zona?: Ellisse | null): void {
    const gl = this.gl;
    if (gl.isContextLost()) throw new Error("contesto WebGL perso");
    if (this.canvas.width !== c.larghezza || this.canvas.height !== c.altezza) {
      this.canvas.width = c.larghezza;
      this.canvas.height = c.altezza;
    }
    gl.viewport(0, 0, c.larghezza, c.altezza);
    gl.useProgram(this.programma);
    gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer);
    const loc = gl.getAttribLocation(this.programma, "a_pos");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, sorgente);
    const comuni = uniformiComuni(c, this.ultime, zona);
    if (c.volto) this.ultime = comuni;
    for (const [k, v] of Object.entries(comuni)) this.imposta(k, v);
    this.imposta("u_ripristinoBocca", [this.ripristinoBocca]);
    for (const [k, v] of Object.entries(this.shader.uniformi?.(c) ?? {})) this.imposta(k, typeof v === "number" ? [v] : v);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  libera(): void {
    const gl = this.gl;
    gl.deleteTexture(this.texture);
    gl.deleteBuffer(this.buffer);
    gl.deleteProgram(this.programma);
  }
}

/** Chiude il contesto WebGL di un canvas e ne libera la memoria (iPhone ha un tetto alla memoria dei canvas). */
export function chiudiContestoGL(canvas: HTMLCanvasElement | OffscreenCanvas): void {
  const gl = canvas.getContext("webgl2") as WebGL2RenderingContext | null;
  gl?.getExtension("WEBGL_lose_context")?.loseContext();
  canvas.width = 0;
  canvas.height = 0;
}
